/**
 * WHAT A PRE-PURCHASE INSPECTION PROMISES, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * Anthony: "pre purchase requests don't need approval to send a quote but
 * they do need to be told it's TBD and done within 1-2 days usually but we
 * confirm with the technician first. Same day is sometimes available and
 * always is in Southern California."
 *
 * Every clause in that sentence is a promise the shop can or cannot keep,
 * and a PPI is the one job where the customer's own deadline is somebody
 * else's — they are standing on a forecourt with a seller waiting. So the
 * timing has to be in the first message, not discovered by ringing up.
 *
 * ─── WHY THE TIME IS TBD AND SAYS SO ───────────────────────────────────
 *
 * A PPI cannot be booked to the minute when it is quoted. The technician
 * has to agree it, the car is at a dealership or a stranger's driveway,
 * and the address is often not confirmed. Promising a slot and moving it
 * is worse than saying plainly that it is not fixed yet, because the
 * customer is making a buying decision around it.
 *
 * SO IT PROMISES A RANGE AND NAMES THE CONDITION. One to two days
 * usually, same day sometimes, and always available in Southern
 * California. Those are the shop's real numbers, not softeners.
 *
 * AND IT NEVER SAYS "CONFIRMED". The word this copy avoids is "booked".
 * Nothing here is booked until a technician has said yes, which is the
 * same rule the rest of the product runs on: a booking with no date sits
 * on no board.
 */

/** Words, lowercased and stripped, so "pre-purchase" and "pre purchase" match. */
const words = (s: unknown) =>
  String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * The names this job goes by. Kept here rather than read from
 * `flat_prices` because this is about the WORDING of a promise, and a shop
 * that renames its price should not silently stop telling customers when
 * they will be seen.
 */
const NAMES = [
  "pre purchase", "prepurchase", "ppi", "buyers inspection",
  "buyer inspection", "used car inspection", "purchase inspection",
];

/** Is this job a pre-purchase inspection, from anything written on it. */
export function isPrePurchase(...text: Array<string | null | undefined>): boolean {
  const t = words(text.filter(Boolean).join(" "));
  if (!t) return false;
  return NAMES.some((n) => t.includes(n));
}

/**
 * SOUTHERN CALIFORNIA IS ALWAYS SAME DAY. Anthony said "always is in
 * Southern California", and the shop is in San Jacinto, so that is home
 * ground rather than a favor. The 9 prefix covers Riverside, San
 * Bernardino, Orange, LA and San Diego; 92 and 93 reach the rest of it.
 */
export function inSoCal(postcode: string | null | undefined, where?: string | null): boolean {
  const zip = String(postcode ?? "").replace(/\D/g, "").slice(0, 5);
  if (zip.length === 5) {
    const n = Number(zip);
    // 90000-93599 is Los Angeles down to San Diego and out to the desert.
    return n >= 90000 && n <= 93599;
  }
  // No zip code, so fall back to what somebody typed about the address.
  const t = words(where);
  return /\b(hemet|san jacinto|riverside|moreno valley|temecula|murrieta|perris|corona|menifee|banning|beaumont|redlands|ontario|anaheim|orange county|los angeles|san diego|inland empire|socal|southern california)\b/
    .test(t);
}

/**
 * The sentence that goes in the text with the price. It is deliberately
 * three short lines: the customer is reading it standing next to a car
 * they are thinking about buying.
 */
export function prePurchaseTiming(sameDay: boolean): string {
  return sameDay
    ? "We can usually get to a pre-purchase inspection the same day around here, "
      + "and one to two days at the outside. We confirm the time with the tech "
      + "before anything is fixed, so treat it as TBD until you hear from us."
    : "Pre-purchase inspections are usually done within one to two days, and "
      + "sometimes the same day. We confirm the time with the tech first, so "
      + "treat it as TBD until you hear from us.";
}

/**
 * The longer version, for the "What this covers" note on their own page.
 * It says what the money buys as well as when, because a PPI is the one
 * quote where a customer is deciding whether to spend thousands on the
 * strength of it.
 */
export function prePurchaseNote(sameDay: boolean): string {
  return [
    "This is a pre-purchase inspection on a car you are thinking of buying.",
    "",
    prePurchaseTiming(sameDay),
    "",
    "The time is not booked yet. We agree it with the technician who is going "
      + "out, then we tell you, and you are welcome to be there.",
    "",
    "You get what we found and what it will cost to put right, so you can "
      + "decide whether to buy it and what to say about the price.",
  ].join("\n");
}
